<script lang="ts" setup generic="TObj extends BaseObj & { EventID?: number; ID: number }, TDetailObj extends TObj = any">
/**
 * T needs to be a BaseObj and have the ID property
 */

import { computed, nextTick, onMounted, ref, unref, type Component } from 'vue';
import type BaseObjListViewCompExpose from '../../objects/BaseObjListViewCompExpose';
import { DropdownItemButton, DropdownItemCustom, DropdownItemMenu, DropdownItemSeparator } from '@/objects';
import { BaseObjDao } from '@/dao/BaseObjDao';
import type { BaseObj } from '@/objects/BaseObj';
import { useEventStoreRef, useToastStore, useUtilStore } from '@/stores';
import { ListComp, ContentComp } from '@/components';
import type { FieldDef, GuiDef } from '@/objects/gui';
import { $tm } from '@/locize';
import { useDialog, useHelper, useKeyboardListener, useTaskLoader } from '@/composeables';
import type ListCompExpose from '@/objects/ListCompExpose';
import type { PostResponse } from '@/objects/generated';
import type { ITranslationKey } from '@/objects/TranslationKey';
import type { GridColumnOptions, GridSetup, IGridColumnSort } from '../Grid/Classes';
import type { ModalSize } from '../Modal/ModalProps';
import type { GridResizeStrategy } from '../Grid/Classes/GridResizeStrategy';
import type { IGridDetailExposed } from '../Grid/Classes/IGridDetailExposed';
import type { IGridRowCssRule } from '../Grid/Classes/IGridRowCssRule';
import { Router } from '@/objects/Router';
import { randomstring } from '@/utilities/utils';
import type { IBaseObjStaticConstructor } from '@/objects/IBaseObjStaticConstructor';
import { arrayDiff } from '@/utilities';

// If the data in the list is valid
let dataisvalid = true;

/**
 * Set up keyboard shortcuts
 */
const { addKeyboardShortcut, removeKeyboardShortcut } = useKeyboardListener();

/**
 * The reference to the list component
 */
const listComp = ref<ListCompExpose<TObj> | null>(null);

/**
 * The state of the component
 */
const loaded = ref(false);

const errorMessage = ref<ITranslationKey | undefined>(undefined);

/**
 * This is used to mark when we are sending data to the server so we can show the saving state
 */
const isSavingUpdating = ref(false);

/**
 * Props for the component
 */
const props = withDefaults(
    defineProps<{
        detailView?: Component<{ id: number }> | null;
        /**
         * The size of the detail view modal.
         */
        detailSize?: ModalSize;

        /**
         * The base type of the objects in the list.
         */
        baseType: IBaseObjStaticConstructor<TObj> & { new (args: any[]): TObj }; // Note: The `& { new (args: any[]): TObj }` is to remove a warning that vue throws at runtime. Basically we say that we are passing a type with a constructor, otherwise it will expect an object, when we send a "function"

        /**
         * The gui definition for the object.
         */
        gui: GuiDef<TObj>;
        modelValue: Array<TObj>;
        selected?: Array<TObj>;

        /**
         * The text to show when the list is loading
         * @remark should be a translation key
         */
        loadingText?: string;

        /**
         * The icon to show when the list is loading
         */
        loadingIcon?: string;

        /**
         * The function to retrieve the name of the object
         * @param obj
         */
        nameGetter?: (obj: TObj) => string;

        /**
         * The plural name of the object
         */
        pluralname?: string;

        /**
         * Custom filter function for the list
         */
        filter?: Array<(obj: TObj, searchstring: string) => boolean>;

        /**
         * The placeholder text for the filter input
         */
        filterPlaceholder?: string;

        /**
         * Custom sorting function for the list
         */
        sort?: boolean | ((a: TObj, b: TObj) => number) | any; // TODO: We probably want default on this and this col here, false will mean no sorting

        /**
         * The actions of the action menu dropdown
         */
        menuActions?: Array<DropdownItemButton | DropdownItemSeparator | DropdownItemMenu | DropdownItemCustom<any, any>>;

        /**
         * The quick buttons presented on the toolbar
         */
        quickButtons?: Array<DropdownItemButton | DropdownItemSeparator | DropdownItemMenu | DropdownItemCustom<any, any>>;

        /**
         * The menu actions to show in the right side of the toolbar
         * @default []
         */
        rightActions?: Array<DropdownItemButton | DropdownItemSeparator | DropdownItemMenu | DropdownItemCustom<any, any>>;

        /**
         * Hide the default buttons in the toolbar. This will hide the Save, Delete and Add buttons.
         */
        hideDefaultButtons?: boolean;

        /**
         * If the add new item button should be hidden
         * @default false
         */
        hideAddButton?: boolean;

        /**
         * If true, when deleting an item, we will set its entity state to deleted and not actually remove it from the list. This is useful when you want to show the deleted items in the list and allow the user to undo the delete action before saving.
         * @default false
         */
        deleteAsState?: boolean;

        /**
         * Will not add the default menu actions to the toolbar
         */
        hideDefaultMenuActions?: boolean;

        /**
         * The custom columns for the grid
         * These are columns that are not in the GUI definition
         * @default []
         */
        customColumns?: Array<GridColumnOptions<TObj>> | ((columns: Array<GridColumnOptions<TObj>>) => Array<GridColumnOptions<TObj>>);

        /**
         * The row css rules to apply to the grid
         */
        rowClassRules?: IGridRowCssRule<TObj>[];

        /**
         * If the list should be sortable or not
         * @default true
         */
        sortable?: boolean;

        /**
         * Load the dataset when th grid is mounted or not
         * @default true
         */
        loadOnMount?: boolean;

        /**
         * Hide the search field from the toolbar
         * @default false
         */
        hideSearchField?: boolean;

        /**
         * Autoresize columns after data is loaded
         * @default false
         */
        autoresizeColumns?: boolean;

        /**
         * If the add new item button should only show the detail modal when we add.
         * This mean that we can not add new items directly in the grid.
         * @default false
         */
        addwithModal?: boolean;

        /**
         * Height
         */
        height?: number;

        /**
         * A unique identifier for the grid. This will enable the grid to remember the column sizes and order and state
         */
        gridkey?: string;

        /**
         * Detail view gui
         * This is used to create a new instance of the detail view with a different gui than the list
         * @default undefined
         */
        detailViewGui?: GuiDef<TObj>;

        /**
         * Can override the default detail view type if we use another type in the deatil view vs the list view
         * @remark This should in some form inherit or extend the actual basetype
         */
        detailViewType?: IBaseObjStaticConstructor<TDetailObj> & { new (args: any[]): TObj };

        /**
         * Additional props to pass to the detail view component
         */
        detailViewParams?: any;

        /**
         * Readonly mode
         * @default false
         */
        readonly?: boolean;

        /**
         * The setupobject for the detail grid when we open a detail row
         * The component will need to implement the IGridDetailExposed interface
         */
        gridDetailSetup?: GridSetup<TDetailObj>;

        /**
         * If we have a ready component for the detail view
         * @default undefined
         */
        gridDetailView?: Component<IGridDetailExposed<TDetailObj>>;

        /**
         * Hide the status bar from the grid
         * @default false
         */
        hideStatusbar?: boolean;

        /**
         * Hide the toolbar from the grid
         * @default false
         */
        hideToolbar?: boolean;

        /**
         * Resize strategy for the grid
         * @default undefined
         */
        resizeStrategy?: GridResizeStrategy;

        /**
         * Loader for the list
         * This is used in case we need to load the list in a different way than the default
         * @default undefined
         */
        listLoader?: () => Promise<void>;

        /**
         * Never show the messages. This will hide the Changes, Field error and Tip messages.
         * This will not affect the cell colors and messages for validations within the grid, only the messageboxes that are displayed on top of the grid
         * @default false
         */
        hideMessages?: boolean;

        /**
         * he title for the detail view in the grid
         * @default undefined
         */
        gridDetailTitle?: string;

        /**
         * The selectall header is changed for a action header
         * @default false
         */
        useActionHeader?: boolean;

        /**
         * If the modal should stay open after saving
         * @default false
         */
        keepOpenOnSave?: boolean;

        /**
         * Custom GUI fields for the object.
         * @param fields The fields to customize.
         */
        customGuiFields?: (fields: FieldDef<TObj>[]) => FieldDef<TObj>[];

        /**
         * The row selection mode for the grid
         */
        rowselection?: 'multiple' | 'single';

        /**
         * The default sort function for the dataset
         */
        defaultSort?: (a: TObj, b: TObj) => number;

        /**
         * Hide deleted items from the list
         * @default false
         */
        hideDeleted?: boolean;

        /**
         * quietLoad: If true, the loading spinners will be hidden
         */
        quietLoad?: boolean;

        /**
         * Actions to show in the footer before the close and save buttons
         */
        detailFooterActions?: Array<DropdownItemButton<(id: number) => any | Promise<any>>>;

        /**
         * Triggered before save, return true to cancel saving
         */
        beforeSave?: (toSave: Array<TObj>, fromDetail?: boolean) => Promise<boolean>;

        /** Text to show in addition when unsaved warning is shown */
        additionalWarningOnChange?: string;

        /**
         * Alternative function for overriding the delete functionality.
         * @param rows The rows to delete.
         * @returns A promise that resolves to an object containing the ids of the deleted items, or null if the default delete functionality should be used.
         */
        deleteSelectedRows?: () => Promise<void>;

        /**
         * When save list is pressed, we only emit event so calling component can handle the save and we dont do any of the default saving behavior
         */
        emitOnlyOnSave?: boolean;
    }>(),
    {
        detailView: null,
        loadingIcon: 'fa-spinner-third',
        nameGetter: () => '',
        filter: () => [],
        selected: () => [],
        sort: true,
        menuActions: () => [],
        quickButtons: () => [],
        hideDefaultButtons: false,
        customColumns: () => [],
        loadOnMount: true,

        autoresizeColumns: false,
        addwithModal: false,
        readonly: false,
        hideStatusbar: false,
        hideToolbar: false,
        hideMessages: false,
        useActionHeader: false,
        keepOpenOnSave: false,
        hideDeleted: false,
        quietLoad: false,
        emitOnlyOnSave: false,
    },
);

const loadingText = $tm(props.loadingText || `${props.gui.Name}.loadingtext`.toLocaleLowerCase());
const pluralname = $tm(props.pluralname || `${props.gui.Name}.pluralname`.toLocaleLowerCase());

/**
 * Emits for the component
 */
const emit = defineEmits<{
    /**
     * v-model update for the modelValue
     */
    (e: 'update:modelValue', obj: Array<TObj>): void;

    /**
     * When the mouse is hovered over an object in the list
     */
    (e: 'objHovered', obj: TObj | null): void;

    /**
     * Emits when an item is added to the dataset
     */
    (e: 'itemAdded', obj: TObj): TObj;

    /**
     * When the rows have changed
     */
    (e: 'rowschanged'): void;

    /**
     * Emits when we have finished saving either the detail view or the list view
     * @param ids The ids of the objects that were saved
     */
    (e: 'savingItemsEnd', ids: Array<number>): void;

    /**
     * Whenever a key is pressed in a cell
     */
    (e: 'cellKeyDown', event: KeyboardEvent): void;

    /**
     * When the data is changed in the list
     */
    (e: 'datachanged', state: boolean): void;

    /**
     * If the detail view is
     */
    (e: 'detailchanged', value: boolean): void;

    /**
     * If the data is valid or not
     */
    (e: 'datastate', valid: boolean): void;

    /**
     * Whenever the selection changes
     */
    (e: 'onSelectionChanged', selectedRows: TObj[]): void;

    /**
     * Emits when the savelist button is pressed
     */
    (e: 'saveList'): void;

    /**
     * Emits whenever a cell is changed
     */
    (e: 'cellValueChanged', row: TObj, columnId: string, newVal: any, oldVal: any): void;

    // We emit this when the detailModal has loaded a new item
    (e: 'detailLoaded', value: TObj | TDetailObj): void;

    (e: 'onCellEditingStopped', row: TObj, columnId: string, rowId?: string): void;
    (e: 'onCellEditingStarted', row: TObj, columnId: string, rowId?: string): void;
}>();

const toastStore = useToastStore();
const { eventID } = useEventStoreRef();

/**
 * The actual list of objects

 */
const list = computed({
    get: () => props.modelValue,
    set: (value) => emit('update:modelValue', value),
});

const loadList = async () => {
    if (props.listLoader) {
        await props.listLoader();
    } else {
        list.value = await BaseObjDao.Load<TObj>(props.baseType, eventID.value, []);
    }
    dataUpdated();
};

/**
 * Data updated
 */
const dataUpdated = async (rowIds?: string[]) => {
    await listComp?.value?.dataUpdated(rowIds);

    /** We need to always update the data from the CustomColumns since we dont know what data they depend on.
     *  The reason is that AGGrids own Change detection only triggers when data in one of the dataFields that are matched to the dataset property has changed.
     *  @see https://www.ag-grid.com/javascript-data-grid/change-detection/
     */
    if (props.customColumns && (rowIds ?? []).length > 0) {
        let columns = [];
        if (props.customColumns instanceof Function) {
            columns = props.customColumns([]);
        } else {
            columns = props.customColumns;
        }
        // These are the columnIds that are custom
        let customColumnIds = columns.filter((x) => x.colId !== undefined).map((x) => x.colId) as string[];

        if (customColumnIds.length > 0) {
            listComp?.value?.refreshCells(rowIds!, customColumnIds!);
        }
    }
};

/**
 * Set up the loader
 */
const { AddLoader, LoadState } = useTaskLoader();

/**
 * We need a loader also for the detail view
 */
const { AddLoader: saveAddLoader, LoadState: saveLoadState } = useTaskLoader();

/**
 * Load dependencies
 */
AddLoader(props.gui.LoadDependencies, 'general.loadingdependencies');

// Load the list on mount if we haven said it is loaded outside
if (props.loadOnMount) {
    AddLoader(loadList, loadingText, props.loadingIcon);
}

/**
 * Save the detail window
 * @param obj
 */
const saveDetail = async (obj: TObj | TDetailObj, refreshOnlyIds?: number[]) => {
    if (refreshOnlyIds && refreshOnlyIds.length > 0) {
        emit('savingItemsEnd', refreshOnlyIds);
        refreshSaved(refreshOnlyIds, false);
        return;
    }

    const detailobj = unref<TObj | TDetailObj>(obj);

    detailobj.EventID = eventID.value;

    /** trigger beforeSave */
    const cancel = props.beforeSave != undefined ? await props.beforeSave([detailobj], true) : false;

    if (cancel) {
        return;
    }

    saveAddLoader(async () => {
        try {
            const resp = await BaseObjDao.Save(props.detailViewType ?? props.baseType, eventID.value, [detailobj]);

            emit('savingItemsEnd', resp!.IDS);

            refreshSaved(resp!.IDS, false);

            listComp.value?.closeDetail();

            toastStore.addSuccess($tm('general.saved') + ' ' + props.nameGetter(detailobj));
        } catch (e) {
            if ((e as Error).cause == '409') {
                listComp.value?.setDetailError((e as Error).message);
            }
        }
    }, 'general.saving');
};

/**
 * Updates the originalDetailObjHash when the detail view is loaded
 * @param obj
 */
const onDetailLoaded = (obj: TObj | TDetailObj) => {
    emit('detailLoaded', obj);
};

/**
 * Save the list
 */
const saveList = async () => {
    if (!isChanged.value && !rowsAreChanged.value && !props.hideMessages) {
        toastStore.addInfo($tm('general.nothingtosave'));
        return;
    }

    // The changed rows in the parent grid
    let changed = unref(list.value.filter((obj: TObj) => obj.EntityState != 'Unchanged'));

    /** trigger beforeSave */
    const cancel = props.beforeSave != undefined ? await props.beforeSave(changed) : false;

    if (cancel) {
        return null;
    }

    // If we have detail grids we need to save them first
    if (props.gridDetailView) {
        // TODO: Maybe we want a response from the saver to make sure all saving is done before we save the main grid
        await listComp.value?.saveGridDetail();
        listComp.value?.clearChanges();
    }

    if (!dataisvalid) {
        toastStore.addError($tm('general.invaliddata'));
        return null;
    }

    if (changed.length == 0) {
        return null;
    }

    isSavingUpdating.value = true;
    let resp: PostResponse | null = null;
    await AddLoader(async () => {
        try {
            resp = await BaseObjDao.Save(props.baseType, eventID.value, changed);

            emit('savingItemsEnd', resp!.IDS);

            // Reset any error message
            errorMessage.value = undefined;

            refreshSaved(resp!.IDS, false);

            listComp?.value?.clearChanges();

            if (!props.hideMessages) {
                toastStore.addSuccess($tm('general.saved') + ' ' + pluralname);
            }
        } catch (ex) {
            if (ex instanceof Error && ex.cause == '409') {
                // 409 = HttpConflict (ConflictException) and we use that one to determine database issues
                errorMessage.value = { key: ex.message, params: { name: pluralname } } as ITranslationKey;
            } else {
                toastStore.addError((ex as any).toString());
            }
        } finally {
            isSavingUpdating.value = false;
        }
    }, 'general.saving');

    return resp;
};

/**
 * Refreshes list with the ids in list, if empty do nothing
 * @param uids
 */
const refreshSaved = async (uids: Array<number>, showToast: boolean = true) => {
    if (uids.length == 0) {
        return;
    }

    // refresh the ones we are told are updated
    const reloaded = await BaseObjDao.Load(props.baseType, eventID.value, uids);

    type Id = number;
    type Index = number;
    // Make a lookup of the current indexes, so we dont have to do a findIndex in the reloaded loop
    const indexLookup = list.value.reduce((acc, obj, index) => {
        acc.set(obj.ID, index);
        return acc;
    }, new Map<Id, Index>());

    // We change the data of the items in place, so we need to create a new array
    reloaded.forEach((obj) => {
        const index = indexLookup.get(obj.ID) ?? -1;
        if (index >= 0) {
            list.value[index] = obj;
        } else {
            list.value.push(obj);
        }
    });

    // Check if there is anyone in the uids that are not in the reload list, then we remove them
    const removed = arrayDiff(
        uids,
        reloaded.map((x) => x.ID),
    );

    const filtered = list.value.filter((x) => !removed.includes(x.ID) && x.EntityState != 'Deleted' && x.ID > 0);

    // if we have a default sort of dataset we apply it here
    if (props.defaultSort) {
        filtered.sort(props.defaultSort);
    }

    // assign dataset
    list.value = filtered;

    dataUpdated(reloaded?.map((x) => x.ID?.toString()));

    if (showToast) {
        toastStore.addInfo($tm('general.saved') + ' ' + pluralname);
    }
};

/**
 * Refresh the list
 * This will reload the list from the server
 * @param ids a list of ids to refresh
 */
const refreshList = async (ids?: number[]) => {
    if (ids && ids.length > 0) {
        await refreshSaved(ids, false);
    } else {
        await AddLoader(loadList, loadingText, props.loadingIcon);
    }
};

/**
 * Delete selected objects
 * This will show a dialog to confirm the delete
 */
const deleteSelected = async () => {
    const selected = unref(listComp?.value?.getSelectedRows()) as Array<TObj>;

    if (selected.length == 0) {
        useToastStore().addWarning($tm('general.no_rows_selected'));
        return;
    }

    // We check if there is any marked as deleted and with id <= 0 and then we just remove then from the datasource
    // without sending them to the server since they will be ignored on the server anyway.
    const toDeleteFromGrid = selected.filter((x) => x.EntityState === 'Added');
    const toDeleteFromServer = selected.filter((x) => x.EntityState !== 'Added');

    if (toDeleteFromGrid.length > 0) {
        selected.forEach((c) => c.Delete());
        list.value = list.value.filter((x) => !toDeleteFromGrid.includes(x));
        dataUpdated();
        console.log('Deleted from grid', toDeleteFromGrid);
        return;
    }

    if (props.deleteAsState) {
        toDeleteFromServer.forEach((c) => c.Delete());
        dataUpdated();
        return;
    }

    useDialog().YesNo({
        title: $tm('general.deleteselected'),
        body: $tm('general.deletedselectedmsg', selected?.length),
        yesCallback: async () => {
            isSavingUpdating.value = true;
            toDeleteFromServer.forEach((c) => c.Delete());
            AddLoader(async () => {
                try {
                    const resp = await BaseObjDao.Save(props.baseType, eventID.value, toDeleteFromServer);

                    emit('savingItemsEnd', resp!.IDS);

                    refreshSaved(resp!.IDS, false);

                    toastStore.addSuccess($tm('general.deleted') + ' ' + selected.length + ' ' + pluralname);
                } catch (ex) {
                    console.log(ex);
                } finally {
                    isSavingUpdating.value = false;
                }
            }, 'general.deleting');

            return true;
        },
    });
};

/**
 * Add a new object to the list
 */
const addItem = async () => {
    if (props.addwithModal) {
        // We just change the route to new
        nextTick(() => Router.NavigateTo({ id: 'new' }));
    } else {
        let obj = await BaseObjDao.LoadOrCreateSingle<TObj>(props.baseType, useUtilStore().nextFakeId(), eventID.value);

        // allow override
        obj = emit('itemAdded', obj) || obj;

        // list.value.push(obj);

        // We add the item to the first row in the grid
        list.value.unshift(obj);

        // TODO: We need to be able to mark a line as new e.g ag-row-new maybe this should be handled in the grid, but then we will always need a status column
        // use AG-GRID modelUpdated
        dataUpdated();

        // Need to wait for the grid to be rendered. A bit hacky, but how else to do it?
        setTimeout(() => listComp?.value?.focuslastadded(), 20);
    }
};

// Emit when the List is hovered
const objHovered = (obj: TObj | null) => emit('objHovered', obj);

/**
 * Set up default buttons for the view
 */
const buttons: Array<DropdownItemButton | DropdownItemSeparator | DropdownItemMenu | DropdownItemCustom<any, any>> = [
    ...(props.hideDefaultButtons
        ? []
        : props.readonly
          ? []
          : [
                DropdownItemButton.create({
                    action: props.emitOnlyOnSave ? () => emit('saveList') : saveList,
                    buttonType: 'Save',
                    class: 'd-none d-lg-block',
                }),
                DropdownItemButton.create({
                    action: props.deleteSelectedRows ?? deleteSelected,
                    buttonType: 'Delete',
                    class: 'd-none d-lg-block',
                }),
                ...(props.hideAddButton
                    ? []
                    : [
                          DropdownItemButton.create({
                              action: addItem,
                              buttonType: 'Secondary',
                              icon: 'fa-grid-2-plus',
                              class: 'd-none d-lg-block',
                          }),
                      ]),
            ]),
    ...props.quickButtons,
];

/**
 * Helper for the change state
 */
const isChanged = ref(false);

/**
 * Computed to check if any rows are changed
 */
const rowsAreChanged = computed(() => list.value.some((obj) => obj.EntityState != 'Unchanged'));

/**
 * When the rows are changed and not saved yet
 */
const rowsChanged = () => {
    emit('rowschanged');
    isChanged.value = true;
};

/**
 * Whenever the detail changed state is changed
 * @param changed
 */
const detailChanged = (changed: boolean) => {
    if (changed === true) {
        isChanged.value = true;
    }

    // We just emit the changed state
    emit('detailchanged', changed);
};

/**
 * Handle the data state from the ListComp
 */
const onDataStateChanged = (valid: boolean) => {
    dataisvalid = valid;

    emit('datastate', dataisvalid);
};

/**
 * Callback when the data is changed
 * @param state
 */
const onDataChanged = (state: boolean) => {
    emit('datachanged', state);
    isChanged.value = state;
};

// Keep a list of the selected rows
const onSelectionChanged = (selectedRows: TObj[]) => {
    emit('onSelectionChanged', selectedRows);
};

/**
 * Called when the view is mounted.
 * NOTE: This is not called when the view is activated again
 */
onMounted(async () => {
    await Promise.all(LoadState.value.NeedsLoading.map((nl) => nl.prom));
    loaded.value = true;
});

/**
 * Callback for when the component is loaded
 * We try and focus the first input
 * @param isLoaded
 */
const listCompIsLoaded = (isLoaded: boolean) => {
    if (isLoaded) useHelper().setFocus();
};

/**
 * We create a unique view id for the component
 */
const uniqueViewId = randomstring('view-');

/**
 * Callback for when the component is visible
 * We use this to add and remove keyboard shortcuts for when the view isvisible
 * @param visible
 */
const isVisible = (visible: boolean) => {
    if (!visible) {
        // Whenever the view looses visibility we remove the keyboard shortcut
        removeKeyboardShortcut(uniqueViewId);
    } else {
        // We add a keyboard shortcut for saving the list
        addKeyboardShortcut({
            alias: uniqueViewId,
            on: 'CTRL+S',
            action: () => saveList(),
        });

        // We add a keyboard shortcut for adding a new item
        addKeyboardShortcut({
            alias: uniqueViewId,
            on: 'CTRL+PLUS',
            action: () => addItem(),
        });

        addKeyboardShortcut({
            alias: uniqueViewId,
            on: 'CTRL+INSERT',
            action: () => addItem(),
        });
    }
};

defineExpose<BaseObjListViewCompExpose<TObj>>({
    dataUpdated,

    refreshList,

    resizeColumns: () => {
        listComp?.value?.resizeColumns();
    },

    /**
     * Save the list
     * This will save the list to the server
     */
    saveList: async (): Promise<PostResponse | null> => {
        return (await saveList()) || null;
    },

    /**
     * Delete all the selected rows.
     */
    deleteSelected: () => {
        deleteSelected();
    },

    /**
     * Stop editing the current cell
     */
    stopEditing: () => {
        listComp?.value?.stopEditing();
    },

    /**
     * Start editing on the specified column and optional row
     * @param columnId
     * @param rowId
     */
    startEditing: (columnId: string, rowId?: string) => {
        listComp?.value?.startEditing(columnId, rowId);
    },

    /**
     * Deselct all rows in the grid
     * This is typical run after a save operation
     * @returns
     */
    deselectAllRows: () => {
        listComp?.value?.deselectAllRows();
    },

    /**
     * Add a new item to the list
     */
    addItem: () => {
        addItem();
    },

    /**
     * Return the selected items in the list. This is provided by the grid
     */
    getSelectedRows: () => {
        return listComp?.value?.getSelectedRows() ?? [];
    },

    /**
     * If it is a master detail grid then we need to save the detail grids that have changes
     * TODO: Should this be its own call or should it be handled in the saveList method?
     * @returns
     */
    saveGridDetail: () => {
        listComp?.value?.saveGridDetail();
    },

    /**
     * Display an error message in the grid
     * @param error TranslationKey of the error message to display. If set to null we remove the error
     * @param params Optional parameters for the translation
     */
    setError: (error: string | null, params?: any) => {
        if (!error) {
            errorMessage.value = undefined;
        } else {
            const err = {
                key: error,
                default: '',
                params: params,
            };
            errorMessage.value = err;
        }
    },

    /**
     * Will refresh and redraw the cells in the grid
     * @param rowIds
     * @param columns
     * @returns
     */
    refreshCells: (rowIds: string[], columns?: string[]) => {
        listComp?.value?.refreshCells(rowIds, columns);
    },

    /**
     * Sets the sort state of the grid
     * This will override any existing sort state
     * @param newSortState The new sorting state
     */
    setSortState: (newSortState: IGridColumnSort[]) => {
        listComp?.value?.setSortState(newSortState);
    },

    /**
     * Export the list to excel
     */
    exportToExcel: async (fileName: string, sheetName: string) => {
        await listComp?.value?.exportToExcel(fileName, sheetName);
    },
});
</script>
<template>
    <ContentComp
        :load-state="LoadState"
        :show-as-modal="loaded"
        :quiet-load="quietLoad && !isSavingUpdating"
        :errormessage="errorMessage"
        class="h-100 flex-fill"
        @is-loaded="listCompIsLoaded"
        @is-visible="isVisible"
    >
        <slot></slot>
        <ListComp
            v-if="loaded"
            class="h-100"
            ref="listComp"
            v-model="list"
            :autoresizeColumns="autoresizeColumns"
            :customColumns
            :detailSize="detailSize"
            :detailView="detailView"
            :detailViewParams="detailViewParams"
            :filter="filter"
            :filterPlaceholder="filterPlaceholder"
            :gridDetailSetup="gridDetailSetup"
            :grid-detail-view="gridDetailView"
            :grid-detail-title="gridDetailTitle"
            :gridkey="gridkey"
            :gui="gui"
            :hideDefaultButtons
            :hideDefaultMenuActions="hideDefaultMenuActions"
            :hideSearchField
            :hide-messages="hideMessages"
            :hide-statusbar="hideStatusbar"
            :hide-toolbar="hideToolbar"
            :keepOpenOnSave="keepOpenOnSave"
            :mark-dirty-rows="true"
            :menuActions="menuActions"
            :quick-buttons="buttons"
            :readonly="readonly"
            :resize-strategy="resizeStrategy"
            :rowClassRules="rowClassRules"
            :savestate="saveLoadState"
            :saveStateLoader="saveAddLoader"
            :select-all="true"
            :sort="sort"
            :use-action-header="useActionHeader"
            @addInGrid="addItem"
            @datastate="onDataStateChanged"
            @datachanged="onDataChanged"
            @objHovered="objHovered"
            @refreshList="refreshList"
            @rowschanged="rowsChanged"
            @detailchanged="detailChanged"
            @saveDetail="(detail: any, refreshOnlyIds?: number[]) => saveDetail(detail, refreshOnlyIds)"
            @detailLoaded="onDetailLoaded"
            @saveList="saveList"
            @cell-key-down="($event: KeyboardEvent) => emit('cellKeyDown', $event)"
            @on-selection-changed="onSelectionChanged"
            @cell-value-changed="
                (row: TObj, columnId: string, newVal: any, oldVal: any) => emit('cellValueChanged', row, columnId, newVal, oldVal)
            "
            :custom-gui-fields="customGuiFields"
            :rowselection="rowselection"
            :hide-deleted="hideDeleted"
            :detail-footer-actions="detailFooterActions"
            @before-save="beforeSave"
            @on-cell-editing-started="(row: TObj, columnId: string, rowId?: string) => emit('onCellEditingStarted', row, columnId, rowId)"
            @on-cell-editing-stopped="(row: TObj, columnId: string, rowId?: string) => emit('onCellEditingStopped', row, columnId, rowId)"
            :additional-warning-on-change="additionalWarningOnChange"
            :right-actions="rightActions"
        >
            <template v-slot:menu-filters><slot name="menu-filters"></slot></template>
        </ListComp>
    </ContentComp>
</template>
